import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChangePassword } from '@/lib/queries';
import { useAuthStore } from '@/store/auth.store';
import { IsoftWordmark } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PageTransition } from '@/components/ui/motion';

export function ChangePasswordPage() {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mut = useChangePassword();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setSession = useAuthStore((s) => s.setSession);
  const token = useAuthStore((s) => s.token);
  const refreshToken = useAuthStore((s) => s.refreshToken);

  const forced = !!user?.mustChangePassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError('La nueva contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (newPassword !== confirm) {
      setError('Las contraseñas no coinciden');
      return;
    }
    try {
      await mut.mutateAsync({ currentPassword, newPassword });
      // Limpia la bandera mustChangePassword en el store local
      if (user && token && refreshToken) {
        setSession({ ...user, mustChangePassword: false }, token, refreshToken);
      }
      navigate('/');
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? 'No se pudo cambiar la contraseña');
    }
  };

  return (
    <PageTransition className="flex min-h-screen items-center justify-center bg-bg px-4">
      <div className="w-full max-w-md anim-fade-up">
        <div className="mb-6 flex justify-center">
          <IsoftWordmark className="h-10" />
        </div>

        <Card className="rounded-xl">
          <CardContent className="p-4 sm:p-6 lg:p-8">
            <div className="flex flex-col items-center text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-subtle text-primary">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-6 w-6"
                >
                  <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <h1 className="mt-4 text-2xl font-bold text-font-primary">
                {forced ? 'Cambia tu contraseña' : 'Actualizar contraseña'}
              </h1>
              <p className="mt-2 text-sm text-font-secondary">
                {forced
                  ? 'Por seguridad, debes establecer una nueva contraseña antes de continuar.'
                  : 'Actualiza tu contraseña actual por una nueva.'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-font-primary">
                  Contraseña actual
                </label>
                <input
                  type="password"
                  required
                  value={currentPassword}
                  onChange={(e) => setCurrent(e.target.value)}
                  className="input h-11"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-font-primary">
                  Nueva contraseña
                </label>
                <input
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNew(e.target.value)}
                  className="input h-11"
                  placeholder="Mínimo 8 caracteres"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-font-primary">
                  Confirmar nueva contraseña
                </label>
                <input
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="input h-11"
                />
              </div>
              {error && (
                <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-error">{error}</div>
              )}
              <Button type="submit" disabled={mut.isPending} className="mt-2 h-11 w-full">
                {mut.isPending ? 'Guardando...' : 'Guardar nueva contraseña'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </PageTransition>
  );
}
